import { Drawer } from '@material-ui/core';
import cx from 'classnames';
import { cloneDeep, isEqual, pick } from 'lodash';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { updateCompanyShowCache, useCompanyShowQuery, useCompanyUpdateMutation } from '@apis/companiesApi';
import { CompanyShowPayload } from '@apis/companiesApi.types';
import { useConfig } from '@core/contexts/ConfigContext';
import { useOptions } from '@core/contexts/OptionsContext';
import { useDispatchPromise } from '@core/hooks/redux';
import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { CompanyAddressForm } from '@modules/crm/components/company-address-form';
import { CompanyDetailsForm, CompanyDetailsFormErrors } from '@modules/crm/components/company-details-form';
import { InvestorProfile, InvestorProfileFormData } from '@modules/crm/components/investor-profile/InvestorProfile';
import { companiesActions } from '@redux/reducers/common/companies/companiesReducer';
import { usersActions } from '@redux/reducers/common/users/usersReducer';
import { companyManageActions } from '@redux/reducers/crm/companies/manage/companiesManageReducer';
import { companiesSelectors } from '@redux/selectors/common/commonCompanies';
import { usersSelectors } from '@redux/selectors/common/commonUsers';
import { companiesSelectors as crmCompaniesSelectors } from '@redux/selectors/crm/companies';
import { CreateCompanyDTO, UpdateCompanyDTO } from '@redux/types/crm/companies/companiesTypes';
import { Button } from '@shared/components/button-new';
import { ChangesDrawerOverlay } from '@shared/components/changes-drawer-overlay';
import { CompanyFormData } from '@shared/components/create-update-contact/CreateUpdateContact.types';
import { DrawerHeader } from '@shared/components/drawer-header';
import { DrawerOverlay, DrawerOverlayTheme } from '@shared/components/drawer-overlay';
import { Flex } from '@shared/components/flex';
import { GroupedOption } from '@shared/components/group-select';
import { MultiGroupSelect } from '@shared/components/group-select-new/multi-group-select';
import { Loading } from '@shared/components/loading';
import { RadioGroup, RadioGroupSerializerType } from '@shared/components/radio-group';
import { MultiSelect } from '@shared/components/select-new/multi-select';
import { emailRegex } from '@shared/constants/common';
import { drawerTransition } from '@shared/constants/drawer';
import { MultiPropertyIcon } from '@shared/icons/multi-property';
import { PlusIcon } from '@shared/icons/plus';
import { Company, DnbCompany } from '@shared/models/company/company';
import { ButtonColour, ButtonSize } from '@shared/types/common/button';
import { someStringRecord } from '@shared/utils/common';

import { LookupCompany, LookupCompanyModes } from './components/lookup-company';
import { MatchedCompanies } from './components/matched-companies';
import { styles } from './CreateUpdateCompanyDrawer.styles';

export enum ManageCompanyMode {
  create = 'create',
  edit = 'edit',
}

export type CompanyFormErrors = CompanyDetailsFormErrors;

export const deserialiseCompanyForm = (company?: Company): CompanyFormData => {
  return {
    primaryAddress: {
      addressLine1: company?.address1 ?? '',
      addressLine2: company?.address2 ?? '',
      country: company?.country ?? '',
      county: company?.county ?? '',
      postcode: company?.postcode ?? '',
      town: company?.town ?? '',
    },
    secondaryAddress: {
      addressLine1: company?.secondary_address1 ?? '',
      addressLine2: company?.secondary_address2 ?? '',
      country: company?.secondary_country ?? '',
      county: company?.secondary_county ?? '',
      postcode: company?.secondary_postcode ?? '',
      town: company?.secondary_town ?? '',
    },
    alias: company?.alias ?? '',
    assignedUsers: company?.assigned_users?.map((user) => user.id) ?? [],
    companyNumber: company?.company_number ?? '',
    companySource: company?.company_source?.toString() ?? '',
    companyType: company?.company_type ?? [],
    description: company?.description ?? '',
    dunsNumber: company?.duns_number ?? '',
    email: company?.email ?? '',
    hasInvestorProfile: company?.has_investor_profile ?? false,
    investor_aum: company?.investor_profile.investor_aum?.toString() ?? '',
    investor_aum_currency: company?.investor_profile.investor_aum_currency ?? '',
    investor_current_holdings: company?.investor_profile.investor_current_holdings ?? '',
    investor_other_details: company?.investor_profile.investor_other_details ?? '',
    investor_ranking: company?.investor_profile.investor_ranking ?? [],
    investor_region: company?.investor_profile.investor_region ?? [],
    investor_type: company?.investor_profile.investor_type ?? [],
    name: company?.name ?? '',
    nationality: company?.nationality ?? '',
    phoneNumber: company?.phone_number ?? '',
    sectors: company?.sectors ?? [],
    status: company?.status?.toString() ?? '',
    tags: company?.tags?.map((tag) => tag.id) ?? [],
    taxId: company?.tax_id ?? '',
    website: company?.domain ?? '',
  };
};

const serialiseCompanyCreate = (formData: CompanyFormData): CreateCompanyDTO => {
  const payload: CreateCompanyDTO = {
    address1: formData.primaryAddress.addressLine1,
    address2: formData.primaryAddress.addressLine2,
    alias: formData.alias,
    company_number: formData.companyNumber,
    company_source: formData.companySource,
    company_type: formData.companyType,
    country: formData.primaryAddress.country,
    county: formData.primaryAddress.county,
    description: formData.description,
    domain: formData.website,
    duns_number: formData.dunsNumber,
    email: formData.email,
    has_investor_profile: formData.hasInvestorProfile,
    name: formData.name,
    nationality: formData.nationality,
    phone_number: formData.phoneNumber,
    postcode: formData.primaryAddress.postcode,
    secondary_address1: formData.secondaryAddress.addressLine1,
    secondary_address2: formData.secondaryAddress.addressLine2,
    secondary_country: formData.secondaryAddress.country,
    secondary_county: formData.secondaryAddress.county,
    secondary_postcode: formData.secondaryAddress.postcode,
    secondary_town: formData.secondaryAddress.town,
    sectors: formData.sectors,
    tax_id: formData.taxId,
    town: formData.primaryAddress.town,
  };

  // Investor Profile
  if (typeof formData.investor_aum !== 'undefined') {
    payload.investor_aum = formData.investor_aum ? parseFloat(formData.investor_aum) : null;
  }
  if (typeof formData.investor_aum_currency !== 'undefined') {
    payload.investor_aum_currency = formData.investor_aum_currency || null;
  }
  if (typeof formData.investor_current_holdings !== 'undefined') {
    payload.investor_current_holdings = formData.investor_current_holdings;
  }
  if (typeof formData.investor_other_details !== 'undefined') {
    payload.investor_other_details = formData.investor_other_details;
  }
  if (typeof formData.investor_ranking !== 'undefined') {
    payload.investor_ranking = formData.investor_ranking;
  }
  if (typeof formData.investor_region !== 'undefined') {
    payload.investor_region = formData.investor_region;
  }
  if (typeof formData.investor_type !== 'undefined') {
    payload.investor_type = formData.investor_type;
  }

  return payload;
};

export const serialiseCompanyUpdate = (formData: Partial<CompanyFormData>) => {
  const payload: Partial<UpdateCompanyDTO> = {};

  if (typeof formData.alias !== 'undefined') {
    payload.alias = formData.alias;
  }
  if (typeof formData.assignedUsers !== 'undefined') {
    payload.assigned_users = formData.assignedUsers.join(',');
  }
  if (typeof formData.companyNumber !== 'undefined') {
    payload.company_number = formData.companyNumber;
  }
  if (typeof formData.companySource !== 'undefined') {
    payload.company_source = formData.companySource;
  }
  if (typeof formData.companyType !== 'undefined') {
    payload.company_type = formData.companyType;
  }
  if (typeof formData.description !== 'undefined') {
    payload.description = formData.description;
  }
  if (typeof formData.website !== 'undefined') {
    payload.domain = formData.website;
  }
  if (typeof formData.dunsNumber !== 'undefined') {
    payload.duns_number = formData.dunsNumber;
  }
  if (typeof formData.email !== 'undefined') {
    payload.email = formData.email;
  }
  if (typeof formData.hasInvestorProfile !== 'undefined') {
    payload.has_investor_profile = formData.hasInvestorProfile;
  }
  if (typeof formData.investor_aum !== 'undefined') {
    payload.investor_aum = formData.investor_aum ? parseFloat(formData.investor_aum) : null;
  }
  if (typeof formData.investor_aum_currency !== 'undefined') {
    payload.investor_aum_currency = formData.investor_aum_currency ?? null;
  }
  if (typeof formData.investor_current_holdings !== 'undefined') {
    payload.investor_current_holdings = formData.investor_current_holdings ?? null;
  }
  if (typeof formData.investor_other_details !== 'undefined') {
    payload.investor_other_details = formData.investor_other_details ?? null;
  }
  if (typeof formData.investor_ranking !== 'undefined') {
    payload.investor_ranking = formData.investor_ranking ?? null;
  }
  if (typeof formData.investor_region !== 'undefined') {
    payload.investor_region = formData.investor_region ?? null;
  }
  if (typeof formData.investor_type !== 'undefined') {
    payload.investor_type = formData.investor_type ?? null;
  }
  if (typeof formData.name !== 'undefined') {
    payload.name = formData.name;
  }
  if (typeof formData.nationality !== 'undefined') {
    payload.nationality = formData.nationality;
  }
  if (typeof formData.phoneNumber !== 'undefined') {
    payload.phone_number = formData.phoneNumber;
  }
  if (typeof formData.sectors !== 'undefined') {
    payload.sectors = formData.sectors;
  }
  if (typeof formData.status !== 'undefined') {
    payload.status = formData.status ? parseInt(formData.status) : null;
  }
  if (typeof formData.tags !== 'undefined') {
    payload.tags = formData.tags.join(',');
  }
  if (typeof formData.taxId !== 'undefined') {
    payload.tax_id = formData.taxId;
  }

  if (typeof formData.primaryAddress !== 'undefined') {
    payload.address1 = formData.primaryAddress.addressLine1;
    payload.address2 = formData.primaryAddress.addressLine2;
    payload.country = formData.primaryAddress.country;
    payload.county = formData.primaryAddress.county;
    payload.postcode = formData.primaryAddress.postcode;
    payload.town = formData.primaryAddress.town;
  }

  if (typeof formData.secondaryAddress !== 'undefined') {
    payload.secondary_address1 = formData.secondaryAddress.addressLine1;
    payload.secondary_address2 = formData.secondaryAddress.addressLine2;
    payload.secondary_town = formData.secondaryAddress.town;
    payload.secondary_county = formData.secondaryAddress.county;
    payload.secondary_country = formData.secondaryAddress.country;
    payload.secondary_postcode = formData.secondaryAddress.postcode;
  }

  return payload;
};

const defaultCompanyData: CompanyFormData = deserialiseCompanyForm();

const defaultErrors: CompanyFormErrors = {
  alias: '',
  companyNumber: '',
  companySource: '',
  companyType: '',
  description: '',
  dunsNumber: '',
  email: '',
  hasInvestorProfile: '',
  investor_aum: '',
  investor_aum_currency: '',
  investor_current_holdings: '',
  investor_other_details: '',
  investor_ranking: '',
  investor_region: '',
  investor_type: '',
  name: '',
  nationality: '',
  phoneNumber: '',
  status: '',
  taxId: '',
  website: '',
};

export interface CreateUpdateCompanyDrawerProps extends WithStyles<typeof styles> {
  companyId?: number;
  initialData?: Partial<CompanyFormData>;
  open: boolean;
  onClose: () => void;
  onOpenDetails: (id: number) => void;
  onCreated?: (company: Company) => void;
  onSaved?: (id: number) => void;
  onUpdated?: (company: Company) => void;
}

const CreateUpdateCompanyDrawerComponent: FC<CreateUpdateCompanyDrawerProps> = ({
  classes,
  companyId,
  initialData,
  open,
  onClose,
  onOpenDetails,
  onCreated,
  onSaved,
  onUpdated,
}) => {
  const dispatch = useDispatch();
  const dispatchPromise = useDispatchPromise();

  const { user } = useConfig();
  const { booleanOptions, companyTagOptions, sectorOptions } = useOptions();

  const commonCompaniesState = useSelector(companiesSelectors.getCompaniesState);
  const state = useSelector(crmCompaniesSelectors.getManageState);
  const usersState = useSelector(usersSelectors.getUsersState);

  const dnbSearch = useMemo(() => user?.organisation?.settings?.dnb === '1', [user?.organisation]);
  const hasCommonCompanies = useMemo(() => !!commonCompaniesState?.companies.length, [commonCompaniesState.companies]);
  const mode = useMemo(
    () => (open && companyId ? ManageCompanyMode.edit : ManageCompanyMode.create),
    [companyId, open]
  );

  // -- Queries --

  const companyShowPayload: CompanyShowPayload = useMemo(
    () => ({
      id: companyId && mode === ManageCompanyMode.edit ? companyId.toString() : '',
      query: { with: 'contacts' },
    }),
    [companyId, mode]
  );

  const companyShowQuery = useCompanyShowQuery(companyShowPayload, {
    // Fetch only when we open
    enabled: false,
  });

  // -- Mutations --

  const companyUpdateMutation = useCompanyUpdateMutation({
    onSuccess: (data) => {
      updateCompanyShowCache(companyShowPayload, () => data);

      if (data.id && onSaved) {
        onSaved(data.id);
      }
      if (data && onUpdated) {
        onUpdated(data);
      }
      handleOpenDetails(data.id);
    },
  });

  const isEditLoading = useMemo(
    () => mode === ManageCompanyMode.edit && companyShowQuery.isLoading,
    [companyShowQuery.isLoading, mode]
  );

  const initialFormData: CompanyFormData = useMemo(() => {
    if (open && companyShowQuery.data && mode === ManageCompanyMode.edit) {
      // console.log('companyShowQuery.data', initialData, companyShowQuery.data);
      return {
        ...initialData,
        ...deserialiseCompanyForm(companyShowQuery.data),
      };
    }

    return { ...defaultCompanyData, ...initialData };
  }, [companyShowQuery.data, initialData, mode, open]);

  const [addressLookupMode, setAddressLookupMode] = useState<boolean>(dnbSearch);
  const [closeAttempted, setCloseAttempted] = useState<boolean>(false);
  const [companyNameFocused, setCompanyNameFocused] = useState<boolean>(false);
  const [editCompanyEnabled, setEditCompanyEnabled] = useState<boolean>(true);
  const [errors, setErrors] = useState<CompanyFormErrors>({ ...defaultErrors });
  const [formData, setFormData] = useState<CompanyFormData>(cloneDeep(defaultCompanyData));
  const [loadingVisible, setLoadingVisible] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [withSecondaryAddress, setWithSecondaryAddress] = useState<boolean>(false);

  const investorFormData: InvestorProfileFormData = useMemo(
    () => ({
      investor_aum: formData.investor_aum,
      investor_aum_currency: formData.investor_aum_currency,
      investor_current_holdings: formData.investor_current_holdings,
      investor_other_details: formData.investor_other_details,
      investor_ranking: formData.investor_ranking,
      investor_region: formData.investor_region,
      investor_type: formData.investor_type,
      nationality: formData.nationality,
    }),
    [formData]
  );

  const disabledFields = useMemo(
    () => companyUpdateMutation.isLoading || !editCompanyEnabled || state.createLoading,
    [companyUpdateMutation.isLoading, editCompanyEnabled, state.createLoading]
  );

  const hasChanges = useMemo(() => {
    return someStringRecord(formData, (value, key) => !isEqual(value, initialFormData[key]));
  }, [formData, initialFormData]);

  const handleReturn = () => {
    dispatch(companiesActions.clearCompaniesList());
  };

  useEffect(() => {
    if (open) {
      setFormData((prev) => ({ ...prev, ...initialData }));

      if (mode === ManageCompanyMode.edit && companyId) {
        setAddressLookupMode(false);
        setEditCompanyEnabled(true);
        companyShowQuery.refetch();
        dispatch(usersActions.getUsersRequest({ limit: '-1', order: 'surname-asc' }));
      }
    } else {
      setTimeout(() => {
        setFormData(cloneDeep(defaultCompanyData));
        setAddressLookupMode(dnbSearch);
        setErrors(cloneDeep(defaultErrors));
        setSubmitted(false);
        setCloseAttempted(false);
        handleReturn();
        setWithSecondaryAddress(false);
      }, drawerTransition);
    }
  }, [open]);

  useEffect(() => {
    setFormData(initialFormData);
  }, [initialFormData]);

  useEffect(() => {
    const _loading = commonCompaniesState.companiesLoading || isEditLoading;

    if (loadingVisible === _loading) {
      return;
    }

    const delayLoading = !loadingVisible && commonCompaniesState.companiesLoading;
    if (delayLoading) {
      // Delay setting loading
      const _timeout = setTimeout(() => {
        setLoadingVisible(_loading);
      }, 300);
      return () => clearTimeout(_timeout);
    } else {
      // Set loading immediately
      setLoadingVisible(_loading);
    }
  }, [commonCompaniesState.companiesLoading, isEditLoading, loadingVisible, mode]);

  useEffect(() => {
    const secondaryAddressFields = pick(companyShowQuery.data, [
      'secondary_address1',
      'secondary_address2',
      'secondary_country',
      'secondary_county',
      'secondary_postcode',
      'secondary_town',
    ]);

    const hasSecondaryAddress = Object.values(secondaryAddressFields).some((f) => !!f);

    setWithSecondaryAddress(hasSecondaryAddress);
  }, [companyShowQuery.data]);

  const validateFormData = (inputsData: CompanyFormData) => {
    const _errors: CompanyFormErrors = { ...defaultErrors };

    if (!inputsData.name) {
      _errors.name = t('please_add_a_company_name');
    }

    if (inputsData.email && !emailRegex.test(inputsData.email)) {
      _errors.email = t('enter_correct_email');
    }

    setErrors(_errors);

    return !someStringRecord(_errors, (value) => !!value);
  };

  const handleCloseAttempted = () => {
    if (hasChanges) {
      setCloseAttempted(true);
    } else {
      onClose();
    }
  };

  const addCompanyWithPredefinedName = useMemo(() => {
    return initialData?.name && mode === ManageCompanyMode.create;
  }, [initialData, mode]);

  const handleFormChange: ChangeCallback<CompanyFormData> = (key) => (value) => {
    setFormData((_prev) => ({ ..._prev, [key]: value }));
  };

  const handleCreateCompany = () => {
    if (!validateFormData(formData)) {
      return;
    }

    // replace this with create mutation
    dispatchPromise(companyManageActions.createCompanyRequest, {
      data: serialiseCompanyCreate(formData),
    }).then((data) => {
      if (onCreated) {
        onCreated(data);
      }
      setSubmitted(true);

      if (addCompanyWithPredefinedName) {
        setTimeout(onClose, 1000);
      }
    });
  };

  const handleUpdateCompany = () => {
    if (!companyId) {
      return;
    }

    if (!validateFormData(formData)) {
      return;
    }

    companyUpdateMutation.mutate({ id: companyId.toString(), body: serialiseCompanyUpdate(formData) });
  };

  const handleOpenDetails = (companyId: number) => {
    onClose();
    onOpenDetails(companyId);
  };

  const handleSelectExistingCompany = (company: Company) => () => {
    onClose();
    onOpenDetails(company.id);
  };

  const handleAddressLookupMode = (lookup: boolean) => {
    if (!lookup) {
      setFormData(defaultCompanyData);
    }
    setAddressLookupMode(lookup);
  };

  const handleSearchAgainClick = () => {
    handleAddressLookupMode(true);
    setFormData((prev) => ({ ...defaultCompanyData, name: prev.name }));
  };

  const handleSelectDnbCompany = (company: DnbCompany) => {
    handleAddressLookupMode(false);

    setFormData((prev) => ({
      ...prev,
      addressLine1: company.address1 || '',
      addressLine2: company.address2 || '',
      companyNumber: '',
      companyType: [],
      country: company.country || '',
      county: company.county || '',
      description: '',
      dunsNumber: company.duns_number || '',
      email: company.email,
      name: company.name,
      phoneNumber: company.phone,
      postcode: company.post_code,
      sectors: [],
      tags: [],
      taxId: company.tax_id,
      town: company.town,
      website: '',
    }));
    setEditCompanyEnabled(false);
  };

  const handleCreateNewCompany = (name: string) => {
    handleAddressLookupMode(false);
    setFormData((prev) => ({ ...prev, name: name }));
    setEditCompanyEnabled(true);
  };

  const handleManuallyCreateCompany = () => {
    setFormData((_prev) => ({
      ..._prev,
      primaryAddress: {
        ..._prev.primaryAddress,
        country: '',
      },
    }));
    handleAddressLookupMode(false);
    setEditCompanyEnabled(true);
  };

  const handleChangeStoreToInvestment = (value: Id) => {
    setFormData((prev) => ({
      ...prev,
      hasInvestorProfile: value === 1,
    }));
  };

  const assignedUsersOptions: GroupedOption[] = useMemo(() => {
    if (!usersState.users.length) {
      return [];
    }

    return [
      {
        label: '',
        options: usersState.users.map((user) => {
          return {
            id: user.id,
            label: user.name,
          };
        }),
      },
    ];
  }, [usersState]);

  const renderCompanyOption = (company: DnbCompany) => {
    const fullAddress = [company.address1, company.town, company.post_code].filter(Boolean).join(', ');

    return (
      <Flex direction="column">
        <span className={classes.companyName}>{company.name}</span>
        {fullAddress && <span className={classes.companyAddress}>{fullAddress}</span>}
      </Flex>
    );
  };

  const drawerHeaderContent = useMemo(() => {
    if (mode === ManageCompanyMode.create) {
      return t('create_a_company');
    }

    const companyNumber = companyShowQuery.data?.phone_number;

    return (
      <Flex wrap="nowrap">
        <Flex alignItems="center" justifyContent="center" autoWidth={false} className={classes.headingIconContainer}>
          <MultiPropertyIcon classes={{ root: classes.headingIcon }} />
        </Flex>
        <Flex
          classes={{ root: classes.headingInfoContainer }}
          direction="column"
          justifyContent={companyNumber ? 'space-between' : 'center'}
        >
          <span className={classes.headingCompanyName}>{companyShowQuery.data?.name}</span>
          {companyNumber && <span className={classes.contactNumber}>{companyNumber}</span>}
        </Flex>
      </Flex>
    );
  }, [companyShowQuery.data, mode]);

  const footerActions = useMemo(() => {
    if (mode === ManageCompanyMode.create) {
      return (
        <>
          {dnbSearch && (
            <Flex>
              <Button text={t('search_again')} onClick={handleSearchAgainClick} />
              {!editCompanyEnabled && (
                <Button
                  classes={{ root: classes.editAddressBtn }}
                  text={t('edit_address')}
                  variant="text"
                  onClick={() => setEditCompanyEnabled(true)}
                />
              )}
            </Flex>
          )}
          <Button
            colour={ButtonColour.primary}
            disabled={companyNameFocused || !!errors.name || commonCompaniesState.companiesLoading}
            loading={state.createLoading}
            text={t('create_company')}
            onClick={handleCreateCompany}
          />
        </>
      );
    }

    return (
      <>
        <Button
          disabled={companyUpdateMutation.isLoading}
          text={t('cancel')}
          onClick={() => {
            if (companyId) handleOpenDetails(companyId);
          }}
        />
        <Button
          classes={{ root: classes.saveBtn }}
          colour={ButtonColour.primary}
          disabled={!hasChanges}
          loading={isEditLoading || companyUpdateMutation.isLoading}
          text={t('save')}
          onClick={handleUpdateCompany}
        />
      </>
    );
  }, [
    commonCompaniesState,
    companyNameFocused,
    companyUpdateMutation.isLoading,
    dnbSearch,
    editCompanyEnabled,
    errors,
    handleUpdateCompany,
    isEditLoading,
    mode,
    state,
  ]);

  const footerActionsPosition = useMemo(() => {
    if (mode === ManageCompanyMode.edit || (mode === ManageCompanyMode.create && !dnbSearch)) {
      return 'flex-end';
    }
    return 'space-between';
  }, [dnbSearch, mode]);

  const content = useMemo(() => {
    if (loadingVisible) {
      return <Loading />;
    }

    if (submitted) {
      return (
        <DrawerOverlay
          buttons={
            addCompanyWithPredefinedName
              ? []
              : [
                  { text: t('exit_now'), onClick: onClose, color: 'default', variant: 'outlined' },
                  {
                    text: t('view_company'),
                    onClick: () => {
                      if (state.companyData) {
                        handleOpenDetails(state.companyData.id);
                      }
                    },
                  },
                ]
          }
          classes={{ root: classes.drawerOverlay }}
          heading={t('company_created')}
          message={t('your_company_was_successfully_created')}
          theme={DrawerOverlayTheme.success}
        />
      );
    }
    if (closeAttempted) {
      return (
        <ChangesDrawerOverlay
          buttons={[
            {
              text: t('leave_form'),
              color: 'default',
              variant: 'outlined',
              onClick: onClose,
            },
            {
              text: t('return_to_form'),
              onClick: () => setCloseAttempted(false),
            },
          ]}
          classes={{ root: classes.drawerOverlay }}
          heading={t('are_you_sure_you_want_to_leave_question')}
          message={t('by_leaving_now_you_will_lose_all_of_your_progress')}
          theme={DrawerOverlayTheme.warning}
        />
      );
    }

    if (addressLookupMode) {
      return (
        <div className={classes.content}>
          <LookupCompany
            search={formData.name}
            mode={LookupCompanyModes.DnB}
            renderCompanyOption={renderCompanyOption}
            onCreateNewCompany={handleCreateNewCompany}
            onManuallyCreate={handleManuallyCreateCompany}
            onSearchChange={handleFormChange('name')}
            onSelectCompany={handleSelectDnbCompany}
          />
        </div>
      );
    }

    if (hasCommonCompanies) {
      return (
        <div className={classes.content}>
          <MatchedCompanies
            handleReturn={handleReturn}
            inputs={formData}
            handleFormChange={handleFormChange}
            companies={commonCompaniesState.companies}
            handleSelect={handleSelectExistingCompany}
          />
        </div>
      );
    }
    return (
      <>
        <div className={classes.content}>
          <div className={classes.block}>
            <span className={classes.blockTitle}>{t('company_details_1')}</span>
            <CompanyDetailsForm
              disabled={disabledFields}
              dunsNumberVisible={dnbSearch}
              errors={errors}
              formData={formData}
              statusVisible={mode === ManageCompanyMode.edit}
              onNameBlur={() => {
                setCompanyNameFocused(false);

                if (mode === ManageCompanyMode.edit || (mode === ManageCompanyMode.create && dnbSearch)) {
                  return;
                }
                if (!validateFormData(formData)) {
                  return;
                }

                setEditCompanyEnabled(true);

                const companySearch = formData.name?.trim() || '';
                if (companySearch) {
                  dispatch(companiesActions.getCompaniesRequest({ search: companySearch }));
                }
              }}
              onNameFocus={() => {
                setCompanyNameFocused(true);
              }}
              setFormData={(data) => setFormData((_prev) => ({ ..._prev, ...data }))}
            />
          </div>
          <div className={classes.block}>
            <span className={classes.blockTitle}>{t('investor_profile')}</span>
            <Flex
              alignItems="center"
              classes={{ root: classes.investorProfileRow }}
              justifyContent="space-between"
              wrap="nowrap"
            >
              <div className={classes.investorProfileElement}>{t('store_an_investment_profile_on_the_company')}</div>
              <div className={classes.investorProfileElement}>
                <RadioGroup
                  disabled={disabledFields}
                  serializerType={RadioGroupSerializerType.number}
                  options={booleanOptions}
                  value={formData.hasInvestorProfile ? 1 : 0}
                  onChange={handleChangeStoreToInvestment}
                  classes={{
                    radios: classes.radioGroup,
                    formControl: classes.radioGroupFrom,
                    formControlLabel: classes.radioGroupLabel,
                  }}
                />
              </div>
            </Flex>
            {formData.hasInvestorProfile && (
              <>
                <div className={classes.divider} />
                <InvestorProfile
                  readonly={disabledFields}
                  values={investorFormData}
                  withLabel={false}
                  onChange={(key) => (value) => setFormData((_prev) => ({ ..._prev, [key]: value }))}
                />
              </>
            )}
          </div>
          <div className={classes.block}>
            <span className={classes.blockTitle}>{t('address')}</span>
            <CompanyAddressForm
              disabled={disabledFields}
              formData={formData.primaryAddress}
              setFormData={(data) => setFormData((_prev) => ({ ..._prev, primaryAddress: data }))}
            />
            {!withSecondaryAddress && (
              <Button
                startIcon={<PlusIcon />}
                text={t('add_secondary_address')}
                classes={{ root: classes.secondaryAddressBtn }}
                onClick={() => setWithSecondaryAddress(true)}
              />
            )}
            {withSecondaryAddress && (
              <>
                <div className={classes.divider} />
                <span className={classes.blockTitle}>{t('secondary_address_1')}</span>
                <CompanyAddressForm
                  disabled={disabledFields}
                  formData={formData.secondaryAddress}
                  setFormData={(data) => setFormData((_prev) => ({ ..._prev, secondaryAddress: data }))}
                />
              </>
            )}
          </div>
          <div className={classes.block}>
            <span className={classes.blockTitle}>
              {t(mode === ManageCompanyMode.create ? 'additional_details' : 'sectors')}
            </span>
            <div className={classes.fullRowElement}>
              {mode === ManageCompanyMode.create && <span className={classes.label}>{t('sectors')}</span>}
              <MultiSelect
                disabled={disabledFields}
                size={ButtonSize.medium}
                value={formData.sectors}
                onChange={(value) => handleFormChange('sectors')(value as string[])}
                options={sectorOptions}
                placeholder={t('please_select')}
                classes={{ root: classes.fullWidthSelect }}
              />
            </div>
          </div>
          {mode === ManageCompanyMode.edit && (
            <>
              <div className={classes.block}>
                <span className={classes.blockTitle}>{t('assigned_users')}</span>
                <MultiGroupSelect
                  disabled={usersState.usersLoading || disabledFields}
                  size={ButtonSize.medium}
                  placeholder={t('xtextx_ellipsis', { text: t(usersState.usersLoading ? 'loading' : 'please_select') })}
                  value={formData.assignedUsers}
                  groupOptions={assignedUsersOptions}
                  onChange={(ids: Id[]) => handleFormChange('assignedUsers')(ids as string[])}
                  classes={{ root: classes.fullWidthSelect }}
                />
              </div>
              {!!companyTagOptions.length && (
                <div className={classes.block}>
                  <span className={classes.blockTitle}>{t('tags')}</span>
                  <MultiSelect
                    disabled={disabledFields}
                    size={ButtonSize.medium}
                    value={formData.tags}
                    onChange={(value) => handleFormChange('tags')(value as number[])}
                    options={companyTagOptions}
                    placeholder={t('please_select')}
                    classes={{ root: classes.fullWidthSelect }}
                  />
                </div>
              )}
            </>
          )}
        </div>
        <Flex
          autoWidth={false}
          alignItems="center"
          justifyContent={footerActionsPosition}
          classes={{ root: classes.footer }}
        >
          {footerActions}
        </Flex>
      </>
    );
  }, [
    addCompanyWithPredefinedName,
    addressLookupMode,
    closeAttempted,
    commonCompaniesState,
    companyShowQuery.data,
    companyTagOptions,
    disabledFields,
    dispatch,
    dnbSearch,
    editCompanyEnabled,
    errors,
    footerActions,
    footerActionsPosition,
    formData,
    hasCommonCompanies,
    loadingVisible,
    mode,
    sectorOptions,
    submitted,
    withSecondaryAddress,
  ]);

  const showHeader = useMemo(
    () => !closeAttempted && !isEditLoading && !submitted,
    [closeAttempted, isEditLoading, submitted]
  );

  return (
    <Drawer
      open={open}
      onClose={handleCloseAttempted}
      anchor="right"
      transitionDuration={drawerTransition}
      classes={{ paper: classes.root }}
    >
      {showHeader && (
        <DrawerHeader
          onClose={handleCloseAttempted}
          header={drawerHeaderContent}
          classes={{
            header: cx(classes.header, { [classes.createHeader]: mode === ManageCompanyMode.create }),
            headerBtn: classes.headerBtn,
            headerIcon: classes.headerIcon,
            withBorder: classes.withBorder,
          }}
        />
      )}
      {content}
    </Drawer>
  );
};

export const CreateUpdateCompanyDrawer = withStyles(styles)(CreateUpdateCompanyDrawerComponent);
